import type { RoleId } from "./roles";
import { ClipboardList, Users, type LucideIcon } from "lucide-react";

export type NavItem = {
  to:
    | "/"
    | "/chat"
    | "/reports"
    | "/recommendations"
    | "/findings"
    | "/activity"
    | "/admin/knowledge-base"
    | "/team";
  label: string;
  icon: LucideIcon;
};

// Building & salkku browsing/CRUD live in the user-menu "Hallitse" overlay, so there is
// no longer a settings nav for them.
// The "Info" group — reference material about how the platform works, plus Kiinteistötiimi (the
// Kairos agent team): not a perspective (it is not a lens on the building; it is the system
// working on it, and its agents fan out to all seven lenses), so it lives here rather than Toiminnot.
export const adminNav: NavItem[] = [
  { to: "/team", label: "Kiinteistötiimi", icon: Users },
  { to: "/admin/knowledge-base", label: "Tietopohja", icon: ClipboardList },
];

export type Greeting = {
  hello: string;
  lead: React.ReactNode | string;
  highlight: string;
  context: string;
};

const roleGreetingBase = {
  owner: {
    headline: "Hyvää huomenta, Salla.",
    bodyParts: {
      before: "Kairos seuraa kiinteistökantaasi ja tunnistaa jatkuvasti ",
      highlight: "uusia optimointimahdollisuuksia",
      after: ". Aloita ylimmästä suosituksesta tai kysy Kairokselta.",
    },
  },
  operations: {
    headline: "Tervetuloa, Salla.",
    bodyParts: {
      before:
        "Kairos vertaa kiinteistöjen kulutusta perustasoon ja nostaa esiin ",
      highlight: "poikkeavat kohteet",
      after: ". Tarkista priorisoidut toimenpiteet.",
    },
  },
  service: {
    headline: "Päivää, Salla.",
    bodyParts: {
      before:
        "Kairos ehdottaa muutoksia ohjaukseen vastuullasi olevissa kohteissa: ",
      highlight: "tarkista avoimet työtehtävät",
      after: ".",
    },
  },
  configuration: {
    headline: "Tervetuloa, Salla.",
    bodyParts: {
      before: "Konfigurointiassistentti on valmis. ",
      highlight: "Tarkista kohteet, joilla on avoimia vyöhykelinkkejä",
      after: ". Avaa kiinteistö ja valitse Konfigurointi-välilehti.",
    },
  },
};

// Same panel title on every role — RecommendationFeed is Suositukset everywhere
// (building Säästöpotentiaali and /recommendations).
const roleRecHeaderBase = {
  owner: {
    title: "Suositukset",
    subtitle: "14 ehdotusta · järjestys: uusimmat ensin",
  },
  operations: {
    title: "Suositukset",
    subtitle: "14 ehdotusta · järjestys: uusimmat ensin",
  },
  service: {
    title: "Suositukset",
    subtitle: "5 avointa ehdotusta · järjestys: uusimmat ensin",
  },
  configuration: {
    title: "Suositukset",
    subtitle: "5 avointa ehdotusta · järjestys: uusimmat ensin",
  },
};

const roleAgentSuggestionsBase = {
  owner: [
    "Mikä on tämän kuun ROI optimointitoimista?",
    "Vertaa salkun A ja B säästöpotentiaalia",
    "Missä syntyy eniten hukkaa juuri nyt?",
  ],
  operations: [
    "Miksi Kairos-malli #7 ennusti 12 % yli toteuman?",
    "Näytä anomaliat lähdedataluokittain",
    "Mitkä syötteet vaikuttavat eniten ennusteeseen?",
  ],
  service: [
    "Mitä konkreettisesti pitää tehdä Hatanpäässä?",
    "Missä BMS-pisteessä säädän IV-aikataulun?",
    "Milloin Logistiikka 4:n laturit pitää tasapainottaa?",
  ],
  configuration: [
    "Mitä konfiguraatiosta puuttuu?",
    "Näytä talotekniikka, joilla ei ole vyöhykelinkkiä",
    "Mitkä riippuvuudet pitää vielä määritellä?",
  ],
};

// The internal digital team reuses the operations content (fleet-ops flavored); it is
// gated to internal identity and never shown in the customer role switcher.
export const roleGreeting: Record<
  RoleId,
  {
    headline: string;
    bodyParts: { before: string; highlight: string; after: string };
  }
> = { ...roleGreetingBase, digital_team: roleGreetingBase.operations };

export const roleRecHeader: Record<
  RoleId,
  { title: string; subtitle: string }
> = { ...roleRecHeaderBase, digital_team: roleRecHeaderBase.operations };

export const roleAgentSuggestions: Record<RoleId, string[]> = {
  ...roleAgentSuggestionsBase,
  digital_team: roleAgentSuggestionsBase.operations,
};
